import { useState, useEffect, useRef } from 'react';
import { MapPin, Search, X, Loader2, Pencil } from 'lucide-react';
import api from '../lib/api';

/**
 * LocationPicker — single smart search field for plan venues.
 *
 * UX: user types naturally ("Starbucks Al Ain", "Zuma DIFC", "Louvre Abu Dhabi").
 * Backend Nominatim proxy returns structured {emirate, region, area, venue}.
 * We surface only one input; selected location renders as a chip with the
 * full address breadcrumb and a clear/edit affordance. Manual entry remains
 * available behind a small "edit manually" toggle for edge cases.
 *
 * Props:
 *   value    — { city, venue, area, region, latitude, longitude }
 *   onChange — (next) => void  (full object)
 */
export default function LocationPicker({ value, onChange, testIdPrefix = 'plan-location' }) {
  const v = value || {};
  const hasLocation = !!(v.venue || v.area || v.city);

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [manualMode, setManualMode] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (!searchOpen) return;
    const t = inputRef.current;
    if (t) {
      const id = requestAnimationFrame(() => t.focus());
      return () => cancelAnimationFrame(id);
    }
  }, [searchOpen]);

  useEffect(() => {
    const trimmed = query.trim();
    if (!searchOpen || trimmed.length < 2) {
      setResults([]);
      setErr('');
      return;
    }
    setLoading(true);
    setErr('');
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/places/search?q=${encodeURIComponent(trimmed)}&limit=8`);
        setResults(data || []);
      } catch {
        setErr('Place search unavailable. Try again or enter manually.');
      }
      setLoading(false);
    }, 280);
    return () => clearTimeout(t);
  }, [query, searchOpen]);

  const selectPlace = (p) => {
    onChange({
      venue: p.venue || p.name || '',
      area: p.area || '',
      region: p.region || '',
      city: p.emirate || '',
      latitude: p.lat ?? null,
      longitude: p.lon ?? null,
    });
    setSearchOpen(false);
    setQuery('');
    setResults([]);
  };

  const clearLocation = () => {
    onChange({ venue: '', area: '', region: '', city: '', latitude: null, longitude: null });
  };

  const updateField = (k, val) => onChange({ ...v, [k]: val });

  // Selected-location breadcrumb
  const breadcrumb = [v.area, v.region, v.city].filter(Boolean).join(' · ');

  if (manualMode) {
    return (
      <div className="space-y-2.5" data-testid={`${testIdPrefix}-manual`}>
        <div className="flex items-center justify-between">
          <label className="block text-xs font-medium text-[#6B6E73] uppercase tracking-wider">Location</label>
          <button
            type="button"
            onClick={() => setManualMode(false)}
            className="text-[11px] font-semibold text-[#3F5200] active:opacity-70"
            data-testid={`${testIdPrefix}-search-mode`}
          >
            Use search
          </button>
        </div>
        <input
          data-testid="plan-venue"
          type="text"
          value={v.venue || ''}
          onChange={e => updateField('venue', e.target.value)}
          placeholder="Venue (e.g., Bla Bla Dubai)"
          className="w-full bg-[#FFFFFF] text-[#111111] rounded-xl px-3 py-2.5 text-sm border border-black/[0.06] focus:outline-none focus:border-[#B6E600]/30 placeholder-[#B0B3B8]"
        />
        <input
          data-testid="plan-area"
          type="text"
          value={v.area || ''}
          onChange={e => updateField('area', e.target.value)}
          placeholder="Area / Neighbourhood (e.g., JBR)"
          className="w-full bg-[#FFFFFF] text-[#111111] rounded-xl px-3 py-2.5 text-sm border border-black/[0.06] focus:outline-none focus:border-[#B6E600]/30 placeholder-[#B0B3B8]"
        />
        <div className="flex gap-2">
          <input
            data-testid="plan-region"
            type="text"
            value={v.region || ''}
            onChange={e => updateField('region', e.target.value)}
            placeholder="Region (e.g., Al Ain)"
            className="flex-1 bg-[#FFFFFF] text-[#111111] rounded-xl px-3 py-2.5 text-sm border border-black/[0.06] focus:outline-none focus:border-[#B6E600]/30 placeholder-[#B0B3B8]"
          />
          <input
            data-testid="plan-city"
            type="text"
            value={v.city || ''}
            onChange={e => updateField('city', e.target.value)}
            placeholder="Emirate"
            className="flex-1 bg-[#FFFFFF] text-[#111111] rounded-xl px-3 py-2.5 text-sm border border-black/[0.06] focus:outline-none focus:border-[#B6E600]/30 placeholder-[#B0B3B8]"
          />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2" data-testid={testIdPrefix}>
      <div className="flex items-center justify-between">
        <label className="block text-xs font-medium text-[#6B6E73] uppercase tracking-wider">Location</label>
        {hasLocation && (
          <button
            type="button"
            onClick={() => setManualMode(true)}
            className="flex items-center gap-1 text-[11px] font-semibold text-[#6B6E73] active:opacity-70"
            data-testid={`${testIdPrefix}-edit-manually`}
          >
            <Pencil size={10} /> Edit manually
          </button>
        )}
      </div>

      {/* Selected chip */}
      {hasLocation && !searchOpen && (
        <div
          className="bg-[#FFFFFF] rounded-2xl border border-black/[0.06] p-3 flex items-start gap-3"
          data-testid={`${testIdPrefix}-selected`}
        >
          <div className="w-9 h-9 rounded-2xl bg-[#B6E600]/15 flex items-center justify-center flex-shrink-0">
            <MapPin size={16} className="text-[#3F5200]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-[#111111] truncate">{v.venue || v.area || 'Location set'}</p>
            {breadcrumb && (
              <p className="text-xs text-[#9B9EA3] truncate mt-0.5">{breadcrumb}</p>
            )}
          </div>
          <button
            type="button"
            onClick={clearLocation}
            className="w-8 h-8 rounded-full flex items-center justify-center active:bg-[#F1F0EA] text-[#9B9EA3] flex-shrink-0"
            data-testid={`${testIdPrefix}-clear`}
            aria-label="Clear location"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Search trigger / search field */}
      {!hasLocation || searchOpen ? (
        <div className="relative" data-testid={`${testIdPrefix}-search`}>
          <div className="flex items-center gap-2 bg-[#FFFFFF] rounded-2xl border border-black/[0.06] px-3 py-2.5 focus-within:border-[#B6E600]/30">
            <Search size={16} className="text-[#9B9EA3] flex-shrink-0" />
            <input
              ref={inputRef}
              data-testid={`${testIdPrefix}-input`}
              type="text"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onFocus={() => setSearchOpen(true)}
              placeholder='Search a place — e.g., "Zuma DIFC", "Starbucks Al Ain"'
              className="flex-1 bg-transparent text-sm text-[#111111] placeholder-[#B0B3B8] focus:outline-none min-w-0"
            />
            {loading && <Loader2 size={14} className="text-[#9B9EA3] animate-spin flex-shrink-0" />}
            {searchOpen && hasLocation && (
              <button
                type="button"
                onClick={() => { setSearchOpen(false); setQuery(''); }}
                className="text-[11px] font-semibold text-[#9B9EA3] flex-shrink-0"
                data-testid={`${testIdPrefix}-search-cancel`}
              >
                Cancel
              </button>
            )}
          </div>

          {searchOpen && (results.length > 0 || (query.trim().length >= 2 && !loading)) && (
            <div
              className="mt-2 bg-[#FFFFFF] rounded-2xl border border-black/[0.06] overflow-hidden divide-y divide-black/[0.04] max-h-[280px] overflow-y-auto"
              data-testid={`${testIdPrefix}-results`}
            >
              {results.map(p => {
                const trail = [p.area, p.region, p.emirate].filter(Boolean).join(' · ');
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => selectPlace(p)}
                    className="w-full flex items-start gap-3 px-3 py-2.5 active:bg-[#F1F0EA] text-left"
                    data-testid={`${testIdPrefix}-result-${p.id}`}
                  >
                    <div className="w-8 h-8 rounded-xl bg-[#B6E600]/15 flex items-center justify-center flex-shrink-0">
                      <MapPin size={14} className="text-[#3F5200]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[#111111] truncate">{p.name}</p>
                      <p className="text-xs text-[#9B9EA3] truncate mt-0.5">
                        {trail || p.address || ''}
                      </p>
                    </div>
                  </button>
                );
              })}
              {!loading && results.length === 0 && query.trim().length >= 2 && !err && (
                <div className="px-4 py-4 text-center">
                  <p className="text-xs text-[#9B9EA3]">No place found.</p>
                  <button
                    type="button"
                    onClick={() => setManualMode(true)}
                    className="mt-2 text-[11px] font-semibold text-[#3F5200] active:opacity-70"
                    data-testid={`${testIdPrefix}-empty-manual`}
                  >
                    Enter location manually
                  </button>
                </div>
              )}
              {err && <div className="px-4 py-3 text-xs text-[#FF3B30]">{err}</div>}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
